import React, { useMemo } from 'react'
import { Select, SIZE_MEDIUM } from '@mds/mds-reactjs-library'
import { numberToWords } from '../../../Utility/SharedUtility'
import {
  buildAssetClassHierarchy,
  parseAssetClassSelectionToUiState,
  resolveAssetClassAttributeIds,
} from './liveDashboardAssetClass'
import Wrapper, { GeographyMultiSelectMenuFix } from './LiveDashboardGeographyFilter.styles'
import { removeParentsWithNoSelectedChildren } from './liveDashboardDimensionUtils'

const selectPortalProps =
  typeof document !== 'undefined'
    ? { menuPortalTarget: document.body, menuPosition: 'fixed', maxMenuHeight: 280 }
    : {}

const AssetClassCollapsedMultiValue = (props) => {
  const { index, selectProps, innerProps } = props
  const selected = Array.isArray(selectProps?.value) ? selectProps.value : []

  if (index !== 0 || selected.length === 0) {
    return null
  }

  const labelText = selected.map((option) => String(option?.label ?? '')).filter(Boolean).join(', ')

  return (
    <div {...innerProps} className="geography-collapsed-multi-value" title={labelText}>
      <span className="geography-collapsed-multi-value__text">{labelText}</span>
    </div>
  )
}

const assetClassMultiSelectProps = {
  ...selectPortalProps,
  collapseTags: true,
  hideSelectedOptions: false,
  closeMenuOnSelect: false,
}

const assetClassMultiSelectComponents = {
  MultiValue: AssetClassCollapsedMultiValue,
}

const normalizeId = (id) => String(id ?? '')

const idsMatch = (left, right) => normalizeId(left) === normalizeId(right)

const LiveDashboardAssetClassFilter = ({
  dimension,
  selection,
  setSelection,
  setValidationMessage,
  dimensionConfiguration,
}) => {
  const hierarchy = useMemo(() => buildAssetClassHierarchy(dimension), [dimension])

  const uiState = useMemo(
    () => parseAssetClassSelectionToUiState(selection?.Attributes, hierarchy),
    [selection?.Attributes, hierarchy],
  )

  const assetClassOptions = useMemo(() => {
    const withSubAssetClasses = hierarchy.assetClassesWithSubAssetClasses.map((assetClass) => ({
      label: assetClass.name,
      value: normalizeId(assetClass.id),
    }))
    const assetClassOnly = hierarchy.assetClassOnlyNodes.map((assetClass) => ({
      label: assetClass.name,
      value: normalizeId(assetClass.id),
    }))
    return [...withSubAssetClasses, ...assetClassOnly]
  }, [hierarchy])

  const subAssetClassOptions = useMemo(() => {
    const options = []
    for (const assetClassId of uiState.selectedAssetClassIds) {
      const assetClass = hierarchy.assetClassesWithSubAssetClasses.find((entry) =>
        idsMatch(entry.id, assetClassId),
      )
      if (!assetClass) {
        continue
      }
      for (const subAssetClass of assetClass.subAssetClasses) {
        options.push({
          label: subAssetClass.name,
          value: normalizeId(subAssetClass.id),
          group: assetClass.name,
        })
      }
    }
    return options
  }, [hierarchy, uiState.selectedAssetClassIds])

  const hasAssetClassWithSubAssetClassesSelected = useMemo(
    () =>
      hierarchy.assetClassesWithSubAssetClasses.some((assetClass) =>
        uiState.selectedAssetClassIds.some((assetClassId) => idsMatch(assetClassId, assetClass.id)),
      ),
    [hierarchy.assetClassesWithSubAssetClasses, uiState.selectedAssetClassIds],
  )

  const validateAndApply = (nextAssetClassIds, nextSubAssetClassIds) => {
    setValidationMessage('')

    const attributeIds = resolveAssetClassAttributeIds(
      nextAssetClassIds,
      nextSubAssetClassIds,
      hierarchy,
    )

    const minSelections = Number.isFinite(dimensionConfiguration?.MinSelections)
      ? dimensionConfiguration.MinSelections
      : 0
    const maxSelections = Number.isFinite(dimensionConfiguration?.MaxSelections)
      ? dimensionConfiguration.MaxSelections
      : Number.MAX_SAFE_INTEGER

    if (attributeIds.length < minSelections) {
      setValidationMessage(
        `Please select at least ${numberToWords(minSelections)} ${
          minSelections === 1 ? 'item' : 'items'
        } for ${dimension.DisplayName}`,
      )
    } else if (attributeIds.length > maxSelections) {
      setValidationMessage(
        `Filter limit exceeded. Select up to ${numberToWords(maxSelections)} ${
          maxSelections === 1 ? 'item' : 'items'
        } for ${dimension.DisplayName}`,
      )
      return
    }

    setSelection(dimension.DimensionID, attributeIds)
  }

  const resolveAssetClassIdsFromValues = (values) =>
    (values ?? []).map((value) => {
      const match =
        hierarchy.assetClassesWithSubAssetClasses.find((assetClass) => idsMatch(assetClass.id, value)) ??
        hierarchy.assetClassOnlyNodes.find((assetClass) => idsMatch(assetClass.id, value))
      return match?.id ?? value
    })

  const onAssetClassChange = (values) => {
    const nextAssetClassIds = resolveAssetClassIdsFromValues(values)

    const previousAssetClassIdSet = new Set(uiState.selectedAssetClassIds.map(normalizeId))
    const nextAssetClassIdSet = new Set(nextAssetClassIds.map(normalizeId))

    let nextSubAssetClassIds = uiState.selectedSubAssetClassIds.filter((subAssetClassId) => {
      const parentAssetClass = hierarchy.assetClassesWithSubAssetClasses.find((assetClass) =>
        assetClass.subAssetClasses.some((subAssetClass) => idsMatch(subAssetClass.id, subAssetClassId)),
      )
      if (!parentAssetClass) {
        return true
      }
      return nextAssetClassIdSet.has(normalizeId(parentAssetClass.id))
    })

    for (const assetClassId of nextAssetClassIds) {
      if (previousAssetClassIdSet.has(normalizeId(assetClassId))) {
        continue
      }

      const assetClass = hierarchy.assetClassesWithSubAssetClasses.find((entry) =>
        idsMatch(entry.id, assetClassId),
      )
      if (!assetClass) {
        continue
      }

      const nextSubAssetClassIdSet = new Set(nextSubAssetClassIds.map(normalizeId))
      for (const subAssetClass of assetClass.subAssetClasses) {
        if (!nextSubAssetClassIdSet.has(normalizeId(subAssetClass.id))) {
          nextSubAssetClassIds.push(subAssetClass.id)
        }
      }
    }

    validateAndApply(nextAssetClassIds, nextSubAssetClassIds)
  }

  const onSubAssetClassChange = (values) => {
    const nextSubAssetClassIds = (values ?? []).map((value) => {
      for (const assetClass of hierarchy.assetClassesWithSubAssetClasses) {
        const match = assetClass.subAssetClasses.find((subAssetClass) =>
          idsMatch(subAssetClass.id, value),
        )
        if (match) {
          return match.id
        }
      }
      return value
    })

    const nextAssetClassIdSet = new Set(uiState.selectedAssetClassIds.map(normalizeId))
    for (const subAssetClassId of nextSubAssetClassIds) {
      const parentAssetClass = hierarchy.assetClassesWithSubAssetClasses.find((assetClass) =>
        assetClass.subAssetClasses.some((subAssetClass) => idsMatch(subAssetClass.id, subAssetClassId)),
      )
      if (parentAssetClass) {
        nextAssetClassIdSet.add(normalizeId(parentAssetClass.id))
      }
    }

    const nextAssetClassIds = removeParentsWithNoSelectedChildren(
      [...nextAssetClassIdSet].map((assetClassKey) => {
        const match =
          hierarchy.assetClassesWithSubAssetClasses.find((assetClass) =>
            idsMatch(assetClass.id, assetClassKey),
          ) ??
          hierarchy.assetClassOnlyNodes.find((assetClass) => idsMatch(assetClass.id, assetClassKey))
        return match?.id ?? assetClassKey
      }),
      nextSubAssetClassIds,
      hierarchy.assetClassesWithSubAssetClasses.map((assetClass) => ({
        id: assetClass.id,
        children: assetClass.subAssetClasses,
      })),
    )

    validateAndApply(nextAssetClassIds, nextSubAssetClassIds)
  }

  const assetClassSelectValue = uiState.selectedAssetClassIds.map(normalizeId)
  const subAssetClassSelectValue = uiState.selectedSubAssetClassIds.map(normalizeId)

  return (
    <Wrapper>
      <GeographyMultiSelectMenuFix />
      <div className="geography-range-row">
        <div className="geography-range-field">
          <label htmlFor={`live-dashboard-asset-class-${dimension.DimensionID}`}>Asset class</label>
          <Select
            inputId={`live-dashboard-asset-class-${dimension.DimensionID}`}
            custom
            multi
            searchable
            clearable
            placeholder="Select asset class(es)"
            size={SIZE_MEDIUM}
            width="100%"
            value={assetClassSelectValue}
            options={assetClassOptions}
            onChange={onAssetClassChange}
            components={assetClassMultiSelectComponents}
            {...assetClassMultiSelectProps}
          />
        </div>
        <div className="geography-range-field">
          <label htmlFor={`live-dashboard-sub-asset-class-${dimension.DimensionID}`}>
            Sub-asset class
          </label>
          <Select
            inputId={`live-dashboard-sub-asset-class-${dimension.DimensionID}`}
            custom
            multi
            searchable
            clearable
            placeholder={
              hasAssetClassWithSubAssetClassesSelected
                ? 'Select sub-asset class(es)'
                : 'Select an asset class with sub-asset classes first'
            }
            size={SIZE_MEDIUM}
            width="100%"
            value={subAssetClassSelectValue}
            options={subAssetClassOptions}
            onChange={onSubAssetClassChange}
            disabled={!hasAssetClassWithSubAssetClassesSelected}
            components={assetClassMultiSelectComponents}
            {...assetClassMultiSelectProps}
          />
        </div>
      </div>
    </Wrapper>
  )
}

export default LiveDashboardAssetClassFilter
